// frontend/src/contexts/PreferenceContext.jsx
// Refactored version with cleaner architecture

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from '../axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  clearAuthLanguageOverride,
  clearInvalidAuthLanguageOverride,
  isLanguageActive,
  normalizeLanguageCode,
  readAuthLanguageOverride
} from '../utils/languageUtils';
import logger from '../utils/logger';

const PreferenceContext = createContext();
const PREFERENCE_CACHE_KEY = 'cachedPreferences';
const PREFERENCE_CACHE_ENTITY_ID_KEY = '__entityId';

const cachePreferencesForEntity = (entityId, preferences) => {
  localStorage.setItem(PREFERENCE_CACHE_KEY, JSON.stringify({
    ...preferences,
    [PREFERENCE_CACHE_ENTITY_ID_KEY]: String(entityId)
  }));
};

const readCachedPreferencesForEntity = (entityId) => {
  const cached = localStorage.getItem(PREFERENCE_CACHE_KEY);
  if (!cached) {
    return null;
  }

  const parsed = JSON.parse(cached);
  if (String(parsed?.[PREFERENCE_CACHE_ENTITY_ID_KEY]) !== String(entityId)) {
    return null;
  }

  const { [PREFERENCE_CACHE_ENTITY_ID_KEY]: _entityId, ...preferences } = parsed;
  return preferences;
};

const isPlainPreferenceRecord = (value) => (
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value)
);

const mergeGlobalPreferencesIntoExistingCache = (globalValues) => {
  try {
    const cached = localStorage.getItem(PREFERENCE_CACHE_KEY);
    if (!cached) return false;

    const parsed = JSON.parse(cached);
    const cacheEntityId = Number(parsed?.[PREFERENCE_CACHE_ENTITY_ID_KEY]);
    if (
      !Number.isInteger(cacheEntityId) ||
      cacheEntityId <= 0 ||
      !isPlainPreferenceRecord(parsed.userGlobal) ||
      !isPlainPreferenceRecord(parsed.userEntity) ||
      !isPlainPreferenceRecord(parsed.entitySettings)
    ) {
      return false;
    }

    // User-global values are user-owned. Preserve the cache's existing
    // personal entity tag and both entity-owned slices verbatim.
    localStorage.setItem(PREFERENCE_CACHE_KEY, JSON.stringify({
      ...parsed,
      userGlobal: {
        ...parsed.userGlobal,
        ...globalValues
      }
    }));
    return true;
  } catch (error) {
    logger.warn('[PreferenceContext] Failed to merge acknowledged global cache values', error);
    return false;
  }
};

const normalizeScopeEntityId = (scope) => {
  const entityId = Number(scope?.entityId);
  return Number.isInteger(entityId) && entityId > 0 ? String(entityId) : null;
};

export const usePreferences = () => {
  const context = useContext(PreferenceContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferenceProvider');
  }
  return context;
};

/**
 * PreferenceProvider - Manages all preference types in a unified way
 * 
 * Preference Types:
 * 1. userGlobalPreferences - User preferences that apply across all entities
 * 2. userEntityPreferences - User preferences specific to an entity
 * 3. entitySettings - Entity-level settings (organization settings)
 * 4. systemSettings - System-level settings (system_preferences table)
 * 
 * Data Flow:
 * 1. Initial load from API (no localStorage defaults)
 * 2. Cache in localStorage for offline/performance
 * 3. Sync with API on changes
 */
export const PreferenceProvider = ({
  children,
  scope = null,
  settingsScope = scope,
  globalPreferenceCarrierScope = scope
}) => {
  const { i18n } = useTranslation();
  
  // Single state object for all preferences
  const [preferences, setPreferences] = useState({
    userGlobal: {},      // User global preferences
    userEntity: {},      // User-entity specific preferences
    entitySettings: {},  // Entity-level settings
    systemSettings: {},  // System-level settings
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const normalizedPreferenceScopeEntityId = normalizeScopeEntityId(scope);
  const normalizedSettingsScopeEntityId = normalizeScopeEntityId(settingsScope);
  const normalizedGlobalCarrierEntityId = normalizeScopeEntityId(globalPreferenceCarrierScope);
  const currentPreferenceEntityIdRef = useRef(normalizedPreferenceScopeEntityId);
  const currentSettingsEntityIdRef = useRef(normalizedSettingsScopeEntityId);
  const currentGlobalCarrierEntityIdRef = useRef(normalizedGlobalCarrierEntityId);
  const renderedPreferenceScopeIdRef = useRef(normalizedPreferenceScopeEntityId);
  const renderedSettingsScopeIdRef = useRef(normalizedSettingsScopeEntityId);
  const renderedGlobalCarrierIdRef = useRef(normalizedGlobalCarrierEntityId);
  const loadedGlobalCarrierIdRef = useRef(null);
  const loadedSettingsEntityIdRef = useRef(null);
  const userEntityOwnerIdRef = useRef(null);
  const entitySettingsOwnerIdRef = useRef(null);
  const hasAcceptedGlobalPreferencesRef = useRef(false);
  const userEntityResultVersionRef = useRef(0);
  const entitySettingsResultVersionRef = useRef(0);
  const globalResultVersionRef = useRef(0);
  const loadingRequestIdRef = useRef(0);
  const globalMutationTailsRef = useRef(new Map());
  const authOverrideAttemptVersionRef = useRef(0);
  const preferencesRef = useRef(preferences);

  // Child effects can run before the provider's passive scope-cleanup effect.
  // Advance each authority during render so no child can start or expose a
  // request under the previous personal/settings owner after App switches.
  if (renderedPreferenceScopeIdRef.current !== normalizedPreferenceScopeEntityId) {
    renderedPreferenceScopeIdRef.current = normalizedPreferenceScopeEntityId;
    currentPreferenceEntityIdRef.current = normalizedPreferenceScopeEntityId;
    userEntityResultVersionRef.current += 1;
    loadingRequestIdRef.current += 1;
  }
  if (renderedSettingsScopeIdRef.current !== normalizedSettingsScopeEntityId) {
    renderedSettingsScopeIdRef.current = normalizedSettingsScopeEntityId;
    currentSettingsEntityIdRef.current = normalizedSettingsScopeEntityId;
    entitySettingsResultVersionRef.current += 1;
    loadingRequestIdRef.current += 1;
    loadedSettingsEntityIdRef.current = null;
    entitySettingsOwnerIdRef.current = null;
  }
  if (renderedGlobalCarrierIdRef.current !== normalizedGlobalCarrierEntityId) {
    renderedGlobalCarrierIdRef.current = normalizedGlobalCarrierEntityId;
    currentGlobalCarrierEntityIdRef.current = normalizedGlobalCarrierEntityId;
    globalResultVersionRef.current += 1;
    loadingRequestIdRef.current += 1;
    loadedGlobalCarrierIdRef.current = null;
  }

  const updatePreferencesState = useCallback((updater) => {
    setPreferences(previous => {
      const next = typeof updater === 'function' ? updater(previous) : updater;
      preferencesRef.current = next;
      return next;
    });
  }, []);

  /**
   * The single localStorage record is a personal-scope cache. Never construct
   * it from independently accepted slices unless both entity-owned owners are
   * the same current actual membership.
   */
  const cacheCompletePreferencesForCurrentScopes = useCallback((nextPreferences) => {
    const preferenceEntityId = currentPreferenceEntityIdRef.current;
    if (
      !preferenceEntityId ||
      currentSettingsEntityIdRef.current !== preferenceEntityId ||
      userEntityOwnerIdRef.current !== preferenceEntityId ||
      entitySettingsOwnerIdRef.current !== preferenceEntityId ||
      !hasAcceptedGlobalPreferencesRef.current
    ) {
      return false;
    }

    cachePreferencesForEntity(preferenceEntityId, nextPreferences);
    return true;
  }, []);

  const syncLegacyUserPreferencesLanguage = useCallback((language) => {
    const normalizedLanguage = normalizeLanguageCode(language);
    if (!normalizedLanguage) return;

    try {
      const storedPreferences = localStorage.getItem('userPreferences');
      const userPreferences = storedPreferences ? JSON.parse(storedPreferences) : {};
      localStorage.setItem('userPreferences', JSON.stringify({
        ...userPreferences,
        language: normalizedLanguage
      }));
    } catch (error) {
      logger.warn('[PreferenceContext] Failed to sync legacy language cache', error);
    }
  }, []);

  const publishAcknowledgedGlobalPreferences = useCallback(async (capturedEntityId, globalValues) => {
    globalResultVersionRef.current += 1;
    hasAcceptedGlobalPreferencesRef.current = true;

    let publishedPreferences;
    updatePreferencesState(previous => {
      const next = {
        ...previous,
        userGlobal: {
          ...previous.userGlobal,
          ...globalValues
        }
      };
      publishedPreferences = next;

      // Prefer a global-only merge into an existing personal cache. If no
      // well-formed cache exists, create one only when Patch 1's complete
      // same-owner gate is already satisfied.
      if (!mergeGlobalPreferencesIntoExistingCache(globalValues)) {
        cacheCompletePreferencesForCurrentScopes(next);
      }
      return next;
    });

    const language = normalizeLanguageCode(globalValues.language);
    if (language) {
      syncLegacyUserPreferencesLanguage(language);
      if (language !== i18n.language) {
        try {
          await i18n.changeLanguage(language);
        } catch (languageError) {
          // Persistence and Context publication are already acknowledged. A
          // transient i18n adapter failure must not report the DB write as
          // uncommitted or reorder the per-key mutation queue.
          logger.warn('[PreferenceContext] Failed to apply acknowledged language to i18n', languageError);
        }
      }
    }

    window.dispatchEvent(new CustomEvent('preferencesUpdated', {
      detail: {
        capturedEntityId,
        values: { userGlobal: globalValues },
        preferences: publishedPreferences
      }
    }));
  }, [
    cacheCompletePreferencesForCurrentScopes,
    i18n,
    syncLegacyUserPreferencesLanguage,
    updatePreferencesState
  ]);

  /**
   * Serialize writes for one user-global key. HTTP response generations alone
   * cannot undo an out-of-order backend commit, so same-key mutations execute
   * in invocation order and publish only after an explicit success envelope.
   */
  const enqueueGlobalPreferenceMutation = useCallback((key, actualValue, options = {}) => {
    const isAuthOverride = options.isAuthOverride === true;
    const isInteractive = options.isInteractive !== false && !isAuthOverride;

    if (key === 'language' && isInteractive) {
      // A later authenticated choice supersedes an auth-page marker that has
      // not yet joined the queue. A queued override is already ordered ahead
      // of this operation and therefore cannot win afterward.
      authOverrideAttemptVersionRef.current += 1;
      clearAuthLanguageOverride();
    }

    const hasExplicitEntityId = options.entityId !== undefined;
    const explicitEntityId = !hasExplicitEntityId
      ? null
      : normalizeScopeEntityId({ entityId: options.entityId });
    const targetEntityId = explicitEntityId || currentGlobalCarrierEntityIdRef.current;
    const explicitTargetIsCurrentSelection = (
      !hasExplicitEntityId ||
      (
        explicitEntityId !== null &&
        currentPreferenceEntityIdRef.current !== null &&
        currentPreferenceEntityIdRef.current === explicitEntityId
      ) ||
      isAuthOverride
    );

    if (
      !targetEntityId ||
      (hasExplicitEntityId && explicitEntityId === null) ||
      !explicitTargetIsCurrentSelection
    ) {
      logger.warn('[PreferenceContext] Cannot update global preference without a valid actual-membership carrier');
      return Promise.resolve(false);
    }

    const priorTail = globalMutationTailsRef.current.get(key) || Promise.resolve();
    const operation = priorTail
      .catch(() => undefined)
      .then(async () => {
        try {
          const response = await axios.put(
            `/api/settings/${targetEntityId}/preferences`,
            { [key]: actualValue }
          );
          if (response?.data?.success !== true) {
            throw new Error('Preference write was not acknowledged');
          }

          await publishAcknowledgedGlobalPreferences(targetEntityId, {
            [key]: actualValue
          });
          return true;
        } catch (mutationError) {
          logger.error(`[PreferenceContext] Failed to update userGlobal.${key}`, mutationError);
          if (options.showErrorToast !== false) {
            toast.error('Failed to save preference');
          }
          return false;
        }
      });

    let trackedTail;
    trackedTail = operation.finally(() => {
      if (globalMutationTailsRef.current.get(key) === trackedTail) {
        globalMutationTailsRef.current.delete(key);
      }
    });
    globalMutationTailsRef.current.set(key, trackedTail);
    return operation;
  }, [publishAcknowledgedGlobalPreferences]);

  const updateGlobalPreference = useCallback((key, actualValue, entityId) => (
    enqueueGlobalPreferenceMutation(key, actualValue, {
      entityId,
      isInteractive: true
    })
  ), [enqueueGlobalPreferenceMutation]);

  /**
   * Auth-page language selection is intentionally session-scoped until a real
   * authenticated preference load is underway. This keeps browser detection or
   * stale localStorage from overwriting a user's saved profile language, and it
   * serializes the override before preferences are applied to state/i18n/cache.
   */
  const applyPendingAuthLanguageOverride = useCallback(async (newPreferences, canApplyOverride) => {
    const override = readAuthLanguageOverride();

    if (override.isPresent && !override.language) {
      clearInvalidAuthLanguageOverride();
      return newPreferences;
    }

    if (!override.language) {
      return newPreferences;
    }

    const authToken = localStorage.getItem('token');
    if (!authToken || !canApplyOverride) {
      return newPreferences;
    }

    const capturedMarker = override.rawValue;
    const capturedAttemptVersion = ++authOverrideAttemptVersionRef.current;
    let activeLanguages = null;
    try {
      const languageResponse = await axios.get('/api/reference/languages');
      if (languageResponse.data?.success && Array.isArray(languageResponse.data.data)) {
        activeLanguages = languageResponse.data.data;
      }
    } catch (error) {
      logger.warn('[PreferenceContext] Failed to verify active languages for auth override', error);
      return newPreferences;
    }

    if (!activeLanguages) {
      return newPreferences;
    }

    const markerAfterValidation = readAuthLanguageOverride();
    if (
      authOverrideAttemptVersionRef.current !== capturedAttemptVersion ||
      markerAfterValidation.rawValue !== capturedMarker
    ) {
      return newPreferences;
    }

    if (!isLanguageActive(override.language, activeLanguages)) {
      clearAuthLanguageOverride();
      return newPreferences;
    }

    const loadedLanguage = normalizeLanguageCode(newPreferences.userGlobal.language);
    const preferencesWithOverride = {
      ...newPreferences,
      userGlobal: {
        ...newPreferences.userGlobal,
        language: override.language
      }
    };

    if (loadedLanguage === override.language) {
      clearAuthLanguageOverride();
      return preferencesWithOverride;
    }

    // Validation can span an entity selection change. Resolve the carrier only
    // at enqueue time, and leave the marker untouched when none is available.
    const currentCarrierEntityId = currentGlobalCarrierEntityIdRef.current;
    if (!currentCarrierEntityId) {
      return newPreferences;
    }

    const saved = await enqueueGlobalPreferenceMutation('language', override.language, {
      entityId: currentCarrierEntityId,
      isAuthOverride: true,
      isInteractive: false,
      showErrorToast: false
    });
    if (!saved) return newPreferences;

    // Clear only the marker this attempt consumed. A different later marker
    // must remain eligible for its own authenticated load.
    if (readAuthLanguageOverride().rawValue === capturedMarker) {
      clearAuthLanguageOverride();
    }
    return preferencesWithOverride;
  }, [enqueueGlobalPreferenceMutation]);
  
  /**
   * Load all preferences from the API
   * No hardcoded defaults - use database defaults
   */
  const loadPreferences = useCallback(async (loadOptions = {}) => {
    const options = typeof loadOptions === 'boolean'
      ? { forceReload: loadOptions }
      : loadOptions;
    const forceReload = options.forceReload === true;
    const carrierEntityId = normalizedGlobalCarrierEntityId;
    const selectedPreferenceEntityId = normalizedPreferenceScopeEntityId;
    const settingsEntityId = normalizedSettingsScopeEntityId;
    const selectedOwnerNeedsReload = (
      options.selectedPersonalChanged === true &&
      selectedPreferenceEntityId !== null &&
      selectedPreferenceEntityId === carrierEntityId &&
      userEntityOwnerIdRef.current !== selectedPreferenceEntityId
    );
    const loadPersonal = (
      carrierEntityId !== null &&
      (
        (
          options.loadPersonal !== false &&
          (forceReload || loadedGlobalCarrierIdRef.current !== carrierEntityId)
        ) ||
        selectedOwnerNeedsReload
      )
    );
    const loadSettings = (
      options.loadSettings !== false &&
      settingsEntityId !== null &&
      (forceReload || loadedSettingsEntityIdRef.current !== settingsEntityId)
    );

    if (!loadPersonal && !loadSettings) {
      if (
        options.selectedPersonalChanged === true ||
        (!carrierEntityId && !settingsEntityId)
      ) {
        logger.debug('[PreferenceContext] No new eligible preference or settings slice, skipping load');
        setLoading(false);
      }
      return;
    }

    if (loadPersonal) loadedGlobalCarrierIdRef.current = carrierEntityId;
    if (loadSettings) loadedSettingsEntityIdRef.current = settingsEntityId;

    const requestId = ++loadingRequestIdRef.current;
    setLoading(true);
    setError(null);

    const personalRequest = loadPersonal ? (async () => {
      const capturedUserEntityVersion = ++userEntityResultVersionRef.current;
      const capturedGlobalVersion = ++globalResultVersionRef.current;

      try {
        const response = await axios.get(`/api/settings/${carrierEntityId}/preferences`);
        if (response?.data?.success !== true || !response.data.data) {
          throw new Error('User preference response was not successful');
        }

        const userGlobal = { ...(response.data.data.global || {}) };
        const userEntity = response.data.data.entity || {};
        const normalizedLanguage = normalizeLanguageCode(userGlobal.language);
        if (userGlobal.language && !normalizedLanguage) {
          userGlobal.language = 'en';
        } else if (normalizedLanguage) {
          userGlobal.language = normalizedLanguage;
        }

        const resolvedPreferences = await applyPendingAuthLanguageOverride(
          { userGlobal, userEntity },
          true
        );
        const entityResultIsCurrent = (
          carrierEntityId === selectedPreferenceEntityId &&
          currentPreferenceEntityIdRef.current === selectedPreferenceEntityId &&
          userEntityResultVersionRef.current === capturedUserEntityVersion
        );
        const globalResultIsCurrent = (
          currentGlobalCarrierEntityIdRef.current === carrierEntityId &&
          globalResultVersionRef.current === capturedGlobalVersion
        );

        if (!entityResultIsCurrent && !globalResultIsCurrent) return;

        if (entityResultIsCurrent) {
          userEntityOwnerIdRef.current = selectedPreferenceEntityId;
        }
        if (globalResultIsCurrent) {
          hasAcceptedGlobalPreferencesRef.current = true;
        }

        updatePreferencesState(previous => {
          const next = {
            ...previous,
            ...(entityResultIsCurrent ? {
              userEntity: resolvedPreferences.userEntity || {}
            } : {}),
            ...(globalResultIsCurrent ? {
              userGlobal: resolvedPreferences.userGlobal || {}
            } : {})
          };
          cacheCompletePreferencesForCurrentScopes(next);
          return next;
        });

        if (globalResultIsCurrent) {
          const language = resolvedPreferences.userGlobal?.language;
          if (language && language !== i18n.language) {
            logger.debug('[PreferenceContext] Syncing preference language');
            await i18n.changeLanguage(language);
          }
          syncLegacyUserPreferencesLanguage(language);
        }
      } catch (requestError) {
        logger.error('[PreferenceContext] Failed to load user preferences', requestError);
        if (currentGlobalCarrierEntityIdRef.current !== carrierEntityId) return;
        setError('Failed to load preferences');

        // The cache is personal-scope-owned. On failure, accept only the record
        // explicitly tagged for this same actual membership.
        try {
          const cachedPreferences = readCachedPreferencesForEntity(carrierEntityId);
          const entityResultIsCurrent = (
            carrierEntityId === selectedPreferenceEntityId &&
            currentPreferenceEntityIdRef.current === selectedPreferenceEntityId &&
            userEntityResultVersionRef.current === capturedUserEntityVersion
          );
          const globalResultIsCurrent = (
            currentGlobalCarrierEntityIdRef.current === carrierEntityId &&
            globalResultVersionRef.current === capturedGlobalVersion
          );
          if (!cachedPreferences || (!entityResultIsCurrent && !globalResultIsCurrent)) {
            return;
          }

          if (entityResultIsCurrent) {
            userEntityOwnerIdRef.current = selectedPreferenceEntityId;
          }
          if (globalResultIsCurrent) {
            hasAcceptedGlobalPreferencesRef.current = true;
          }
          updatePreferencesState(previous => {
            const next = {
              ...previous,
              ...(entityResultIsCurrent ? {
                userEntity: cachedPreferences.userEntity || {}
              } : {}),
              ...(globalResultIsCurrent ? {
                userGlobal: cachedPreferences.userGlobal || {}
              } : {})
            };
            cacheCompletePreferencesForCurrentScopes(next);
            return next;
          });
          logger.debug('[PreferenceContext] Using cached personal preferences');
        } catch (cacheError) {
          logger.error('[PreferenceContext] Failed to parse cached preferences', cacheError);
        }
      }
    })() : Promise.resolve();

    const settingsRequest = loadSettings ? (async () => {
      const capturedSettingsVersion = ++entitySettingsResultVersionRef.current;

      try {
        const response = await axios.get(`/api/settings/entity/${settingsEntityId}`);
        const resultIsCurrent = (
          currentSettingsEntityIdRef.current === settingsEntityId &&
          entitySettingsResultVersionRef.current === capturedSettingsVersion
        );
        if (
          !resultIsCurrent ||
          response?.data?.success !== true ||
          !response.data.data
        ) {
          if (resultIsCurrent && response?.data?.success !== true) {
            throw new Error('Entity settings response was not successful');
          }
          return;
        }

        entitySettingsOwnerIdRef.current = settingsEntityId;
        updatePreferencesState(previous => {
          const next = {
            ...previous,
            entitySettings: response.data.data
          };
          cacheCompletePreferencesForCurrentScopes(next);
          return next;
        });
        logger.debug('[PreferenceContext] Entity settings loaded');
      } catch (requestError) {
        logger.error('[PreferenceContext] Failed to load entity settings', requestError);
        if (
          currentSettingsEntityIdRef.current !== settingsEntityId ||
          entitySettingsResultVersionRef.current !== capturedSettingsVersion
        ) {
          return;
        }

        // A cached settings slice is usable only when this readable settings
        // scope is also the personal scope that owns the cache record.
        if (currentPreferenceEntityIdRef.current !== settingsEntityId) return;
        try {
          const cachedPreferences = readCachedPreferencesForEntity(settingsEntityId);
          if (!cachedPreferences?.entitySettings) return;

          entitySettingsOwnerIdRef.current = settingsEntityId;
          updatePreferencesState(previous => {
            const next = {
              ...previous,
              entitySettings: cachedPreferences.entitySettings
            };
            cacheCompletePreferencesForCurrentScopes(next);
            return next;
          });
          logger.debug('[PreferenceContext] Using cached entity settings');
        } catch (cacheError) {
          logger.error('[PreferenceContext] Failed to parse cached preferences', cacheError);
        }
      }
    })() : Promise.resolve();

    try {
      // Both requests start above, but each owns its own publication and
      // failure path. Awaiting them separately keeps their result identities
      // independent instead of reconstructing one composite response.
      await personalRequest;
      await settingsRequest;
    } finally {
      if (loadingRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [
    applyPendingAuthLanguageOverride,
    cacheCompletePreferencesForCurrentScopes,
    i18n,
    normalizedGlobalCarrierEntityId,
    normalizedPreferenceScopeEntityId,
    normalizedSettingsScopeEntityId,
    syncLegacyUserPreferencesLanguage,
    updatePreferencesState
  ]);
  
  /**
   * Update a specific preference
   * Supports both old format: updatePreference(key, value) 
   * and new format: updatePreference(type, key, value)
   * @param {string} typeOrKey - Type ('userGlobal', etc.) or key for backward compatibility
   * @param {string|any} keyOrValue - Key (new format) or value (old format)
   * @param {any} value - Value (new format only)
  */
  const updatePreference = useCallback(async (typeOrKey, keyOrValue, value) => {
    // Handle backward compatibility: updatePreference(key, value)
    let type, key, actualValue;
    if (value === undefined) {
      // Old format: updatePreference(key, value)
      key = typeOrKey;
      actualValue = keyOrValue;

      // Determine type based on key (for backward compatibility)
      // Only known entity-specific keys need special handling
      const entitySpecificKeys = ['default_launch_page', 'is_primary'];

      if (entitySpecificKeys.includes(key)) {
        type = 'userEntity';
      } else {
        // Default to userGlobal for all other keys
        // New global preferences will automatically work!
        type = 'userGlobal';
      }
      logger.debug('[PreferenceContext] Backward compatibility preference update', { key, type });
    } else {
      // New format: updatePreference(type, key, value)
      type = typeOrKey;
      key = keyOrValue;
      actualValue = value;
    }
    
    logger.debug('[PreferenceContext] Updating preference', { type, key });

    if (type === 'userGlobal') {
      return enqueueGlobalPreferenceMutation(key, actualValue, {
        isInteractive: true
      });
    }

    const entityId = currentPreferenceEntityIdRef.current;
    if (!entityId) {
      logger.error('[PreferenceContext] Cannot update entity-owned preference without entityId');
      return false;
    }
    
    // Optimistically update local state
    const previousPreferences = preferencesRef.current;
    const newPreferences = {
      ...previousPreferences,
      [type]: {
        ...previousPreferences[type],
        [key]: actualValue
      }
    };
    updatePreferencesState(newPreferences);
    
    try {
      // Determine the correct API endpoint based on type
      let endpoint, payload;
      
      switch (type) {
        case 'userEntity': {
          // Entity-scoped user preferences (e.g., default_launch_page, is_primary)
          if (key === 'is_primary') {
            if (actualValue) {
              endpoint = `/api/settings/primary-entity/${entityId}`;
              payload = {};
            } else {
              endpoint = `/api/settings/entity/${entityId}/preference`;
              payload = { preference: key, value: actualValue };
            }
          } else {
            endpoint = `/api/settings/entity/${entityId}/preference`;
            payload = { preference: key, value: actualValue };
          }
          break;
        }
          
        case 'entitySettings':
          endpoint = `/api/settings/entity/${entityId}`;
          payload = { [key]: actualValue };
          break;
          
        case 'systemSettings':
          endpoint = `/api/notification-settings/${entityId}/system-availability`;
          payload = { [key]: actualValue };
          break;
          
        default:
          throw new Error(`Unknown preference type: ${type}`);
      }
      
      await axios.put(endpoint, payload);
      
      // The cache remains a complete actual-membership snapshot; a readable
      // synthetic settings scope must never be folded into this write.
      cacheCompletePreferencesForCurrentScopes(newPreferences);
      
      // Emit event for components that need immediate updates
      window.dispatchEvent(new CustomEvent('preferencesUpdated', { 
        detail: { type, key, value: actualValue, preferences: newPreferences }
      }));
      
      return true;
    } catch (error) {
      logger.error(`[PreferenceContext] Failed to update ${type}.${key}`, error);
      
      // Revert on error
      updatePreferencesState(previousPreferences);
      toast.error('Failed to save preference');
      return false;
    }
  }, [
    cacheCompletePreferencesForCurrentScopes,
    enqueueGlobalPreferenceMutation,
    updatePreferencesState
  ]);

  /**
   * Publish values acknowledged by a component-owned request without issuing
   * another request or recovering a target from mutable storage.
   */
  const publishReceivedPreferenceSuccess = useCallback(async (capturedEntityId, values = {}) => {
    const capturedId = String(capturedEntityId);
    const globalValues = values.userGlobal || {};
    const entityValues = values.userEntity || {};
    const hasGlobalValues = Object.keys(globalValues).length > 0;
    const entityScopeIsCurrent = (
      currentPreferenceEntityIdRef.current !== null &&
      currentPreferenceEntityIdRef.current === capturedId
    );
    const hasCurrentEntityValues = entityScopeIsCurrent && Object.keys(entityValues).length > 0;

    if (hasGlobalValues) {
      await publishAcknowledgedGlobalPreferences(capturedId, globalValues);
    }
    if (hasCurrentEntityValues) {
      userEntityResultVersionRef.current += 1;
      userEntityOwnerIdRef.current = capturedId;
    }

    if (!hasCurrentEntityValues) return;

    let publishedPreferences;
    updatePreferencesState(previous => {
      const next = {
        ...previous,
        userEntity: { ...previous.userEntity, ...entityValues }
      };
      publishedPreferences = next;

      cacheCompletePreferencesForCurrentScopes(next);
      return next;
    });

    window.dispatchEvent(new CustomEvent('preferencesUpdated', {
      detail: {
        capturedEntityId,
        values: { userEntity: entityValues },
        preferences: publishedPreferences
      }
    }));
  }, [
    cacheCompletePreferencesForCurrentScopes,
    publishAcknowledgedGlobalPreferences,
    updatePreferencesState
  ]);
  
  /**
   * Refresh entity settings (useful when entity settings change)
   */
  const refreshEntitySettings = useCallback(async () => {
    const entityId = currentSettingsEntityIdRef.current;
    if (!entityId) return;
    const capturedSettingsVersion = ++entitySettingsResultVersionRef.current;

    try {
      const response = await axios.get(`/api/settings/entity/${entityId}`);
      const scopeIsCurrent = (
        currentSettingsEntityIdRef.current === entityId &&
        entitySettingsResultVersionRef.current === capturedSettingsVersion
      );
      if (scopeIsCurrent && response.data.success && response.data.data) {
        entitySettingsOwnerIdRef.current = entityId;
        updatePreferencesState(previous => {
          const next = {
            ...previous,
            entitySettings: response.data.data
          };
          cacheCompletePreferencesForCurrentScopes(next);
          return next;
        });

        window.dispatchEvent(new CustomEvent('entitySettingsUpdated', {
          detail: response.data.data
        }));
      }
    } catch (error) {
      logger.error('[PreferenceContext] Failed to refresh entity settings', error);
    }
  }, [cacheCompletePreferencesForCurrentScopes, updatePreferencesState]);

  useEffect(() => {
    currentPreferenceEntityIdRef.current = normalizedPreferenceScopeEntityId;
    currentSettingsEntityIdRef.current = normalizedSettingsScopeEntityId;
    currentGlobalCarrierEntityIdRef.current = normalizedGlobalCarrierEntityId;

    if (
      !normalizedPreferenceScopeEntityId &&
      !normalizedSettingsScopeEntityId &&
      !normalizedGlobalCarrierEntityId
    ) {
      setLoading(false);
      setError(null);
    }
  }, [
    normalizedGlobalCarrierEntityId,
    normalizedPreferenceScopeEntityId,
    normalizedSettingsScopeEntityId
  ]);

  // Refresh entity settings when window regains focus (catches admin changes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentSettingsEntityIdRef.current) {
        logger.debug('[PreferenceContext] Window visible, refreshing entity settings');
        refreshEntitySettings();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshEntitySettings]);

  // Refs retain accepted slices across unrelated request settlement, while the
  // public projection exposes an entity-owned slice only to its current owner.
  // This closes the render before passive cleanup when selection changes.
  const ownerCheckedUserEntity = (
    normalizedPreferenceScopeEntityId &&
    userEntityOwnerIdRef.current === normalizedPreferenceScopeEntityId
  ) ? preferences.userEntity : {};
  const ownerCheckedEntitySettings = (
    normalizedSettingsScopeEntityId &&
    entitySettingsOwnerIdRef.current === normalizedSettingsScopeEntityId
  ) ? preferences.entitySettings : {};
  const ownerCheckedPreferences = {
    ...preferences,
    userEntity: ownerCheckedUserEntity,
    entitySettings: ownerCheckedEntitySettings
  };
  
  const value = {
    // All preferences in a structured object
    preferences: ownerCheckedPreferences,
    
    // Convenience accessors for backward compatibility
    userPreferences: preferences.userGlobal,
    entityPreferences: ownerCheckedUserEntity,
    entitySettings: ownerCheckedEntitySettings,
    systemSettings: preferences.systemSettings,
    
    
    // Methods
    updatePreference,
    updateGlobalPreference,
    publishReceivedPreferenceSuccess,
    refreshEntitySettings,
    loadPreferences,
    
    // State
    loading,
    error
  };
  
  return (
    <PreferenceContext.Provider value={value}>
      {children}
    </PreferenceContext.Provider>
  );
};
